import { useEffect, useRef, useState } from 'react';
import { useRecipient } from '../components/settings/shared/settings-queries';
import { useCredits } from './use-credits';
import {
  askStartedAtKey,
  pendingAskKey,
  shouldAskForPhone,
  shouldClearLatch,
  gateSkipVisible,
  shouldRaiseLatch,
} from './post-purchase-phone';

/**
 * The post-purchase phone ask, bound to live data. Every DECISION lives in `post-purchase-phone.ts`
 * as a plain function; this file only supplies it with values and persists the latch. Contract shared
 * with iOS and Android: `docs/claude/post-purchase-phone-verification.md`.
 */

/**
 * Every localStorage access is wrapped, because it throws outright in a private window and with site
 * data blocked, and an exception here would take down the chrome that mounts this hook. A store we
 * cannot read degrades to "no latch, no start instant" — which means we do not ask, and if we are
 * already asking, the Skip shows. Both failure directions stay on the side of not trapping anyone.
 */
function readStore(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Returns whether the write actually landed — the gate's Skip rule needs to know. */
function writeStore(key: string, value: string): boolean {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    /* A latch we cannot persist degrades to asking only within this session, never to a crash. */
    return false;
  }
}

function clearStore(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export interface PostPurchasePhoneGate {
  /** Whether the blocking verification screen should be up. */
  open: boolean;
  /** Whether the way out is visible yet — false for the first minute. */
  canSkip: boolean;
  /** Dismiss for this entry. The latch stays, so it returns at the next open while there is no phone. */
  skip: () => void;
}

export function usePostPurchasePhoneGate(): PostPurchasePhoneGate {
  const { data: recipient } = useRecipient();
  const credits = useCredits();

  // `phoneKnown` is the difference between "no number" and "we have not read one yet". Answering the
  // second as the first puts the ask in front of somebody who already gave us a number.
  const phoneKnown = !!recipient;
  const hasPhone = !!recipient?.phone;
  // Keyed on the metasite, which IS the identity here (the Recipient carries no id on this wire
  // payload) and is what every other per-account key in this app uses.
  const userId = recipient?.metaSiteId ?? null;

  // isFreePlan is absent while the read is in flight; premium is only asserted once it is known.
  const planKnown = !credits.loading && !credits.failed && credits.account != null;
  const isPremium = planKnown && credits.account?.isFreePlan === false;

  // null until the first settled observation — `shouldRaiseLatch` treats that as "no transition", which
  // is what keeps the existing paid base out of this.
  const wasPremium = useRef<boolean | null>(null);

  const [latched, setLatched] = useState(false);
  const [skipped, setSkipped] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  // Hydrate the latch once the user is known. Reading it back is what makes a tab closed the instant a
  // purchase lands still produce the ask, rather than losing it with the page.
  useEffect(() => {
    if (!userId) return;
    setLatched(readStore(pendingAskKey(userId)) === '1');
  }, [userId]);

  useEffect(() => {
    if (!userId || !planKnown || !phoneKnown) return;

    if (shouldClearLatch({ hasPhone, phoneKnown })) {
      clearStore(pendingAskKey(userId));
      clearStore(askStartedAtKey(userId));
      setLatched(false);
      wasPremium.current = isPremium;
      return;
    }

    if (shouldRaiseLatch({ wasPremium: wasPremium.current, isPremium, hasPhone, phoneKnown })) {
      writeStore(pendingAskKey(userId), '1');
      setLatched(true);
    }
    wasPremium.current = isPremium;
  }, [userId, planKnown, phoneKnown, hasPhone, isPremium]);

  const open = !skipped && latched && shouldAskForPhone({ becamePremium: latched, hasPhone, phoneKnown });

  // Stamp the start instant the first time the screen goes up, and never again while it is up. Storing
  // the INSTANT rather than counting ticks is what stops a remount — a tab switch, a re-render, a
  // restored process — from silently restarting the minute.
  //
  // `couldPersist` is remembered because it changes the Skip rule: an instant we cannot store cannot
  // bound the wait across remounts, so the gate reveals Skip rather than risk holding somebody
  // indefinitely. See `gateSkipVisible`.
  const couldPersist = useRef(true);
  useEffect(() => {
    if (!open || !userId) return;
    if (readStore(askStartedAtKey(userId)) === null) {
      couldPersist.current = writeStore(askStartedAtKey(userId), String(Date.now()));
    }
  }, [open, userId]);

  // Re-render while the gate is up so the Skip appears on its own, without an interaction. The interval
  // only drives repainting; the elapsed time itself comes from the stored instant, so a missed tick
  // delays the reveal by a second rather than losing it.
  useEffect(() => {
    if (!open) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [open]);

  // Read during render, not from state: the effect above runs AFTER the first paint, so a render that
  // treated the not-yet-written instant as "unknown" revealed Skip immediately and the 60-second block
  // never happened at all. `gateSkipVisible` is where that distinction lives.
  const rawStart = userId ? readStore(askStartedAtKey(userId)) : null;
  const parsedStart = rawStart === null ? null : Number(rawStart);
  const canSkip = gateSkipVisible({
    storedStartedAtMs: parsedStart !== null && Number.isFinite(parsedStart) ? parsedStart : null,
    // No `userId` means no key to stamp under, so the instant can never be written and every tick
    // would read as "just started" — a block with no end, which is the one outcome this feature may
    // not produce. Folded in with the storage failure because it is the same fact: we cannot bound
    // the wait, so we do not impose one.
    couldPersist: userId !== null && couldPersist.current,
    nowMs: now,
  });

  return {
    open,
    canSkip,
    skip: () => {
      // The start instant is cleared, not the latch: this entry is over, but the person still has no
      // number, so the next open asks again — and asks with a fresh minute rather than an expired one.
      if (userId) clearStore(askStartedAtKey(userId));
      setSkipped(true);
    },
  };
}
